/**
 * Black Forest Flowers — arrangement tier + add-ons.
 *
 * The tier and the add-ons are separate PRODUCTS (Arrangement Upgrade / Mylar Balloon / Ceramic Mug),
 * so they cannot ride along on the bouquet's own /cart/add form — that endpoint adds exactly one
 * product. Rather than reimplementing Minimog's add-to-cart flow (spinner, drawer, notifications,
 * section re-render), we hook the two events it already exposes:
 *
 *   product-ajax:added   fired on document ONLY after a successful add   -> add the extras
 *   product-ajax:error   fired on a failed add                           -> discard that snapshot
 *   cart:refresh         MCartItems listens, re-fetches + re-renders the cart, opens the drawer
 *
 * The tier is a fixed-price VARIANT (Deluxe $15 / Premium $30), never a quantity, so the price is
 * server-side and cannot be desynced from the label.
 */
(function () {
  'use strict';

  /**
   * Selections are queued FIFO, not held in one slot.
   * A shopper can double-click Add to cart, or add a second product before the first extras call
   * resolves. With a single slot the first success consumes the only snapshot and the second add
   * silently gets no upgrade — two Premium bouquets, one upgrade paid. A queue keeps each add matched
   * to its own selection.
   */
  var queue = [];

  /**
   * The form's id, read as an ATTRIBUTE — never as `form.id`.
   *
   * HTMLFormElement exposes its named controls as properties on the form itself, and every Shopify
   * add-to-cart form contains `<input name="id">` (the selected variant). That control SHADOWS the
   * `id` IDL attribute, so `form.id` returns an HTMLInputElement, not a string. Concatenating it into
   * a selector produced `[data-form-id="[object HTMLInputElement]"]`, which matched nothing — the
   * picker came back null, every selection was silently read as "Standard, no add-ons", and shoppers
   * were charged for the bouquet alone. getAttribute() is not shadowable.
   */
  function formId(form) {
    if (!form || typeof form.getAttribute !== 'function') return '';
    return form.getAttribute('id') || '';
  }

  /**
   * Bind the picker to ITS form. Without this, a quick-view add of product B could consume the
   * selection made on product A's page and bill an upgrade "For: A" onto B's add.
   *
   * A product section renders MORE than one /cart/add form — the main one and the sticky add-to-cart
   * bar (`sticky-atc-form-<section id>`) — and both are real buttons a shopper can press. The root
   * lists every form id it governs in `data-form-ids`. The section-id suffix match is the backstop:
   * every form the theme renders for this section ends with the section id, so a future theme update
   * that renames a form still resolves instead of silently dropping the upgrade.
   */
  function pickerFor(form) {
    var id = formId(form);
    if (!id) return null;

    var roots = document.querySelectorAll('[data-bff-extras]');
    var i;

    for (i = 0; i < roots.length; i++) {
      var listed = (roots[i].getAttribute('data-form-ids') || roots[i].getAttribute('data-form-id') || '').split(/\s+/);
      if (listed.indexOf(id) !== -1) return roots[i];
    }

    for (i = 0; i < roots.length; i++) {
      var section = roots[i].getAttribute('data-section-id') || '';
      if (section && id.slice(-section.length) === section) return roots[i];
    }

    return null;
  }

  /**
   * Bouquet quantity, read through form.elements.
   * Minimog's quantity input is bound with the HTML5 `form=` attribute and lives OUTSIDE the <form>,
   * so form.querySelector('[name=quantity]') finds nothing while FormData still submits it. That is
   * how "2 bouquets, Premium" once billed a single bouquet's upgrade.
   */
  function parentQuantity(form) {
    if (!form || !form.elements) return 1;
    var field = form.elements.namedItem ? form.elements.namedItem('quantity') : form.elements.quantity;
    var raw = field && typeof field.value === 'string' ? field.value : '';
    var n = parseInt(raw, 10);
    return n > 0 ? n : 1;
  }

  function newGroupToken() {
    return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function readSelection(root, form) {
    if (!root) return null;

    var tier = root.querySelector('[data-bff-tier-variant]:checked');
    var tierVariant = tier ? tier.getAttribute('data-bff-tier-variant') : '';

    var addons = [];
    root.querySelectorAll('[data-bff-addon]:checked').forEach(function (box) {
      var id = box.getAttribute('data-variant-id');
      if (id) addons.push(id);
    });

    if (!tierVariant && !addons.length) return null;

    // Stamp the group token onto the bouquet's own submission, so the upgrade can be tied to the exact
    // bouquet LINE (two identical bouquets for two recipients, one upgraded — the title alone cannot
    // tell them apart).
    //
    // A hidden input can only be bound to ONE form via the HTML5 `form=` attribute, so the snippet
    // renders one per form the root governs. Stamp the one belonging to the form actually submitting;
    // stamping the wrong one posts an empty token on the bouquet line.
    var token = newGroupToken();
    var id = formId(form);
    var hidden =
      (id && root.querySelector('[data-bff-group][data-for-form="' + id + '"]')) ||
      root.querySelector('[data-bff-group]');
    if (hidden) hidden.value = token;

    return {
      tierVariant: tierVariant,
      quantity: parentQuantity(form),
      addons: addons,
      token: token,
      parentTitle: root.getAttribute('data-parent-title') || '',
    };
  }

  function hasSelection(root) {
    if (!root) return false;
    var tier = root.querySelector('[data-bff-tier-variant]:checked');
    if (tier && tier.getAttribute('data-bff-tier-variant')) return true;
    return root.querySelectorAll('[data-bff-addon]:checked').length > 0;
  }

  function buildItems(sel) {
    var items = [];

    if (sel.tierVariant) {
      // One upgrade unit PER BOUQUET; the variant carries the price (Deluxe $15 / Premium $30).
      items.push({
        id: Number(sel.tierVariant),
        quantity: sel.quantity,
        properties: { For: sel.parentTitle, _bff_group: sel.token },
      });
    }

    // Add-ons are per ORDER ("add something extra to this delivery"), so one each regardless of
    // bouquet quantity. No properties, so a second balloon merges into one line at quantity 2.
    sel.addons.forEach(function (id) {
      items.push({ id: Number(id), quantity: 1 });
    });

    return items;
  }

  function refreshCart(openDrawer) {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { open: openDrawer === true } })
    );
  }

  function cartAddUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/add.js';
  }

  function addExtras(sel) {
    var items = buildItems(sel);
    if (!items.length) return;

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        // Never treat a 4xx as success. Shopify answers /cart/add.js with JSON on failure too, and a
        // 422 can have PARTIALLY mutated the cart — so tell the shopper and re-render rather than
        // leaving them believing they bought an upgrade that is not there.
        if (!response.ok) throw new Error('cart/add.js returned ' + response.status);
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('bff:extras:added'));
        refreshCart(true);
      })
      .catch(function (err) {
        console.error('[bff] could not add extras', err);
        document.dispatchEvent(new CustomEvent('bff:extras:error'));
        refreshCart(true);
        window.alert(
          'Sorry — we could not add your upgrade or add-on, so it is NOT in your cart. ' +
            'Your flowers were added. Please check your cart and add the extra again.'
        );
      });
  }

  // Snapshot at submit. Runs at document capture AFTER bff-delivery-fields.js: if that guard blocks the
  // add it calls stopImmediatePropagation(), so this never runs and no snapshot is queued for an add
  // that did not happen.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.getAttribute !== 'function') return;
      if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
      queue.push(readSelection(pickerFor(form), form));
    },
    true
  );

  document.addEventListener('product-ajax:added', function () {
    if (!queue.length) return;
    var sel = queue.shift();
    if (sel) addExtras(sel);
    resetPicker();
  });

  // A failed add must drop its snapshot, or the next successful add would inherit it and bill an
  // upgrade nobody chose.
  document.addEventListener('product-ajax:error', function () {
    if (queue.length) queue.shift();
  });

  function resetPicker() {
    document.querySelectorAll('[data-bff-extras]').forEach(function (root) {
      var standard = root.querySelector('[data-bff-tier-variant=""]');
      if (standard) standard.checked = true;
      root.querySelectorAll('[data-bff-addon]:checked').forEach(function (box) {
        box.checked = false;
      });
      // Clear every token input (one per form), not just the first — a stale token left on the
      // sticky bar's input would tie the NEXT bouquet line to an upgrade the shopper did not repick.
      root.querySelectorAll('[data-bff-group]').forEach(function (hidden) {
        hidden.value = '';
      });
      var note = root.querySelector('[data-bff-extras-note]');
      if (note) note.hidden = true;
    });
  }

  /**
   * Express checkout (Shop Pay / PayPal / Google Pay / "Buy it now") cannot carry the extras.
   * Those buttons never fire `submit` — they hand the single product straight to checkout — so a
   * shopper who picked Premium and a balloon would pay for, and receive, just the bouquet. There is no
   * way to attach separate line items to that flow, so when anything extra is selected we block the
   * button and point them at Add to cart. With nothing extra selected it behaves exactly as before.
   */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest('.m-product-dynamic-checkout')) return;

      var root = document.querySelector('[data-bff-extras]');
      if (!hasSelection(root)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var note = root.querySelector('[data-bff-extras-note]');
      if (note) {
        note.hidden = false;
        note.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    true
  );

  var checkoutPending = false;
  var checkoutWaitsForExtras = false;
  var checkoutButton = null;

  function resetCheckout() {
    checkoutPending = false;
    checkoutWaitsForExtras = false;
    if (checkoutButton) checkoutButton.disabled = false;
    checkoutButton = null;
  }

  function goToCheckout() {
    if (!checkoutPending) return;
    checkoutPending = false;
    window.location.assign('/checkout');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var button = target && target.closest && target.closest('[data-bff-checkout-now]');
    if (!button || checkoutPending) return;
    var form = document.getElementById(button.getAttribute('data-form-id'));
    var add = form && form.querySelector('button[type="submit"][name="add"]');
    if (!add || add.disabled || !form.reportValidity()) return;
    checkoutWaitsForExtras = hasSelection(pickerFor(form));
    checkoutPending = true;
    checkoutButton = button;
    button.disabled = true;
    add.click();
    if (document.querySelector('.bff-df__input--invalid')) {
      resetCheckout();
      return;
    }
    window.setTimeout(function () {
      if (checkoutPending) resetCheckout();
    }, 30000);
  });

  document.addEventListener('product-ajax:added', function () {
    if (checkoutPending && !checkoutWaitsForExtras) goToCheckout();
  });

  document.addEventListener('bff:extras:added', function () {
    if (checkoutPending && checkoutWaitsForExtras) goToCheckout();
  });

  document.addEventListener('product-ajax:error', resetCheckout);
  document.addEventListener('bff:extras:error', resetCheckout);
})();
